import { Component, Inject, ChangeDetectionStrategy } from '@angular/core'
import {
    Action,
    compareDates,
    ConfirmationService,
    Entry,
    ErrorsT,
    FieldTracker,
    FluentTracker,
    im,
    isPositiveInt,
    isZeroOrPositiveInt,
    LabelProvider,
    LibraryHelper,
    selfValuesFactory,
    SmartColumns,
    stringToMap,
    Table,
    TableControlType,
    TableDelegate,
    TableExportType,
    Via
} from '@surgetech-eu/core'
import { merge, Observable, of } from 'rxjs'
import moment from 'moment'
import uuid from 'uuid'
import { finalize, map, mergeMap, tap } from 'rxjs/operators'
import { BaseMaterialItem, BaseMaterialService } from '../services/base-material.service'
import { disabledColumnOptions, maxRank } from '../constant'
import { MaterialFamilyService } from '../services/material-family.service'
import { LibraryModel } from '../models'
import { SortRank } from '../helpers'
import { ActivatedRoute } from '@angular/router'

@Component({
    selector: 'app-base-material',
    templateUrl: './base-material.component.html',
    styleUrls: ['./base-material.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@SmartColumns()
export class BaseMaterialComponent implements TableDelegate {
    private table: Table

    saving = false
    saved = false
    changed = false
    error: ErrorsT = null
    private noErrorsInEdited: boolean;

    private fluentTracker: FluentTracker
    private pendingSource: BaseMaterialItem[] = []
    private materialFamilySource: LibraryModel[]
    private materialFamilyOptions = new Map<string, string>()
    private labels: LabelProvider
    importUrl = '/libraries/management/types/baseMaterial/import-entries'
    constructor(
        @Inject('surge.core.schema-transformers') private schemaTransformers: any[],
        private baseMaterialService: BaseMaterialService,
        private confirmationService: ConfirmationService,
        private materialFamilyService: MaterialFamilyService,
        route: ActivatedRoute
    ) {
        const data = route.snapshot.data
        this.labels = data.labels
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                key: {
                    required: true,
                    validate: (value, extra, withinNs) => {
                        if (!value) {
                            return "It's required"
                        }
                        if (
                            this.pendingSource.findIndex(
                                item =>
                                    item.key.toLowerCase().trim() === value.toLowerCase().trim() &&
                                    item.id.trim() !== withinNs.trim()
                            ) !== -1
                        ) {
                            return 'Should have a unique value'
                        }
                        return null
                    }
                },
                description: {
                    required: false
                },
                materialFamily: {
                    required: true,
                    validate: (v, extra, withinNs) => {
                        if (!v) {
                            return "It's required"
                        }
                        if (v && typeof v === 'string') {
                            const findT = this.materialFamilySource.find(
                                tItem => tItem.key.toLowerCase() === v.toLowerCase()
                            )
                            if (!this.materialFamilyOptions.get(v.toLowerCase()) && v) {
                                return im('{value} is not found', { value: findT?.key + '-' + findT?.description })
                            }
                        }
                        if (v && typeof v === 'object') {
                            if (!this.materialFamilyOptions.get(v.id.toLowerCase()) && v) {
                                return v ? im('{value} is not found', { value: LibraryHelper.libraryAttributeToStringOption(v)}) : null
                            }
                        }
                        return null
                    }
                },
                rank: {
                    required: false,
                    validate: value => {
                        if (value === null) {
                            return null
                        }
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return im('It must be less than {maxRank}', { maxRank } )
                        }
                        return null
                    }
                },
                disabled: {
                    required: false
                },
                new: {
                    required: false
                },
                library: {
                    required: true
                },
                namespace: {
                    required: true
                },
                changed: {
                }
            }
        })
        this.fluentTracker.listen(
            FluentTracker.EVENT_CHANGE,
            () => {
                const data = this.fluentTracker.generate()
                this.noErrorsInEdited = data.every(row => (!row.changed && !row.new) || !this.fluentTracker.hasErrors(row.id))

                this.error = null
                this.table.refreshSizes()
            },
            true
        )
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getColumnsConfig() {
        return [
            {
                name: 'key',
                title: this.labels.get('libraries.baseMaterialTable.key'),
                type: 'text'
            },
            {
                name: 'description',
                title: this.labels.get('libraries.baseMaterialTable.description'),
                type: 'text'
            },
            {
                name: 'materialFamily',
                title: this.labels.get('libraries.baseMaterialTable.materialFamily'),
                type: 'text'
            },
            {
                name: 'createdAt',
                title: this.labels.get('libraries.baseMaterialTable.createdAt'),
                type: 'date'
            },
            {
                name: 'createdBy',
                title: this.labels.get('libraries.baseMaterialTable.createdBy'),
                type: 'text'
            },
            {
                name: 'updatedAt',
                title: this.labels.get('libraries.baseMaterialTable.updatedAt'),
                type: 'date'
            },
            {
                name: 'updatedBy',
                title: this.labels.get('libraries.baseMaterialTable.updatedBy'),
                type: 'text'
            },
            {
                name: 'rank',
                title: this.labels.get('libraries.baseMaterialTable.rank')
            },
            {
                name: 'disabled',
                title: this.labels.get('libraries.baseMaterialTable.disabled')
            }
        ]
    }

    customizeColumns(columnsMap) {
        const keyColumn = columnsMap.get('key')
        keyColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => {
                this.updateFluentValue(value, row, 'key')
            },
            canShow: (value, row) => row.new,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'key'),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'key'),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, 'key') || row.key
            }
        }
        keyColumn.filter = {
            via: Via.TEXT
        }

        const descriptionColumn = columnsMap.get('description')
        descriptionColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => {
                this.updateFluentValue(value, row, 'description')
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'description'),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'description'),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, 'description') || row.description
            }
        }
        descriptionColumn.filter = {
            via: Via.TEXT
        }
        const materialFamilyColumn = columnsMap.get('materialFamily')
        materialFamilyColumn.control = {
            type: TableControlType.SELECT,
            values: (v, row) => {
                const options = new Map<string, string>()
                this.materialFamilySource.sort(SortRank).map(item => {
                    if (!item.disabled || row.type?.id === item.key) {
                        options.set(item.key, item.description ? item.key + ' - ' + item.description : item.key)
                    }
                })
                if (v && !this.materialFamilyOptions.get(v.id)) {
                    options.set(v.id, v.code ? v.id + ' - ' + v.code : v.id)
                }
                return options
            },
            change: (value, row) => {
                const find = this.materialFamilySource.find(item => item.key === value)
                if (find) {
                    value = {
                        id: find.key,
                        code: find.description
                    }
                }
                this.updateFluentValue(value, row, 'materialFamily')
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'materialFamily'),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'materialFamily'),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, 'materialFamily')?.id || row.materialFamily?.id
            }
        }
        materialFamilyColumn.filter = {
            via: Via.TEXT
        }

        const createdAtColumn = columnsMap.get('createdAt')
//         createdAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        createdAtColumn.filter = {
            via: Via.MOMENT
        }
        createdAtColumn.sorting = {
            sortable: true,
            compare: compareDates('createdAt')
        }

        const createdByColumn = columnsMap.get('createdBy')
        createdByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('createdBy', stringToMap),
            path: 'createdBy'
        }

        const updatedAtColumn = columnsMap.get('updatedAt')
//         updatedAtColumn.pipe = v => (v ? moment(v).format(DD/MM/YYYY) : )
        updatedAtColumn.filter = {
            via: Via.MOMENT
        }
        updatedAtColumn.sorting = {
            sortable: true,
            compare: compareDates('updatedAt')
        }

        const updatedByColumn = columnsMap.get('updatedBy')
        updatedByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('updatedBy', stringToMap),
            path: 'updatedBy'
        }

        const rankColumn = columnsMap.get('rank')
        rankColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => {
                this.updateFluentValue(value, row, 'rank')
            },
            canShow: () => true,
            failed: (v, row) => this.fluentTracker.tracker.hasError(row.id, 'rank'),
            failure: (v, row) => this.fluentTracker.tracker.getError(row.id, 'rank'),
            seed: row => {
                return this.fluentTracker.tracker.getValue(row.id, 'rank') || row.rank
            }
        }
        rankColumn.sorting = {
            sortable: false
        }
        rankColumn.filter = null

        const disabledColumn = columnsMap.get('disabled')
        disabledColumn.control = {
            type: TableControlType.DROPDOWN,
            values: disabledColumnOptions,
            required: true,
            change: (value, row) => {
                this.updateFluentValue(value, row, 'disabled')
            },
            disabled: () => false,
            canShow: () => true,
            seed: row => this.fluentTracker.tracker.getValue(row.id, 'disabled') || row.disabled?.toString()
        }
        disabledColumn.sorting = {
            sortable: false
        }
        disabledColumn.filter = null
    }

    canTableRemove(): boolean {
        return true
    }

    canTableExport(): boolean {
        return true
    }

    getTableExportTypes(): TableExportType[] {
        return [
            {
                name: 'xlsx',
                url: '/libraries/management/types/baseMaterial/export-entries/xlsx'
            }
        ]
    }

    shouldTableExportDirectly() {
        return true
    }

    mapBeforeTableExportDirectly(rows: any[]): any[] {
        this.fluentTracker.validateAll()
        if (!this.fluentTracker.hasErrors()) {
            return rows.map(item => {
                delete item.__index
                delete item.__internalRowNumbers
                delete item.__sortIndex
                delete item.new
                return item
            })
        } else {
            return rows
                .map(item => {
                    delete item.__index
                    delete item.__internalRowNumbers
                    delete item.__sortIndex
                    return item
                })
                .filter(item => !item.new)
        }
    }

    generateTableExportFilename(type: TableExportType): string {
        return 'BC-' + moment().format('YYYY-MM-DD') + '-Handbook-Base/PlatingMaterialKEYE.xlsx'
    }

    get importTemplateUrl() {
        return `/libraries/management/types/baseMaterial/import-entries/template.xlsx`
    }

    importFailed(error) {
        if (typeof error === 'string') {
            this.error = error
        } else {
            this.error = 'Unknown error'
        }
    }

    beforeImport() {
        this.error = null
        this.saved = false
    }

    afterImport(imported) {
        if (imported.length > 0) {
            const entriesByKey: { [key: string]: Entry } = {}
            this.table.getAllData().forEach(row => (entriesByKey[row.key] = row))

            let hasDuplicates = false
            let hasRankOverflow = false
            let hasNotIntRank = false
            imported.forEach(entry => {
                const found = entriesByKey[entry.key]
                if (found) {
                    hasDuplicates = true
                }
                if (entry.rank && !isZeroOrPositiveInt(entry.rank)) {
                    hasNotIntRank = true
                }
                if (entry.rank > maxRank) {
                    hasRankOverflow = true
                }

                const newItem = Object.assign({}, this.createNewBaseMaterialType(), entry)
                Object.keys(newItem).forEach(value => {
                    if (value === 'disabled') {
                        this.updateValue(newItem.id, value, newItem[value].toString())
                    } else if (value === 'materialFamily') {
                        this.updateValue(newItem.id, 'materialFamily', newItem.materialFamily)
                        if (!this.materialFamilyOptions.get(newItem[value].id?.toLowerCase())) {
                            this.fluentTracker.tracker.addError(
                                newItem.id,
                                'materialFamily',
                                im('{value} is not found', { value: LibraryHelper.libraryAttributeToStringOption(newItem[value]) })
                            )
                        } else if (!newItem[value]?.id) {
                            this.fluentTracker.tracker.addError(newItem.id, 'materialFamily', "It's required")
                        }
                        return
                    } else {
                        this.updateValue(newItem.id, value, newItem[value])
                    }
                })
                this.pendingSource.push(newItem)
                this.updateExistFields(newItem)
            })
            let error: ErrorsT = []
            if (hasDuplicates) {
                error.push('The key must be unique.')
            }
            if (hasNotIntRank || hasRankOverflow) {
                error.push(im('Rank should be a positive number and less than {maxRank}.', { maxRank }))
            }
            if (error?.length) {
                error.push('Please check the data.')
            }
            if (this.fluentTracker.hasErrors()) {
                if (!error.length) {
                    error.push('Some of the imported entries have errors. Please check the data.')
                }
                this.error = error
            }
            this.table.goToLastPage()
            this.table.reload()
        }
    }

    configureTableRemoveAction(action: Action) {
        action.disabled = (row: BaseMaterialItem) => row.isUsed
        action.run = (row: BaseMaterialItem) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            return of(true)
                        }
                        delete row.__index
                        delete row.__internalRowNumbers
                        delete row.__sortIndex
                        return this.baseMaterialService.remove([row]).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                this.error = error
                            }
                        )
                    })
                    .catch(() => {
                        observer.next(true)
                    })
            })
        }
    }

    getTableData(): Observable<BaseMaterialItem[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }
        return this.materialFamilyService
            .getAll()
            .pipe(
                mergeMap(materialFamily => {
                    this.materialFamilySource = materialFamily
                    materialFamily.forEach(item => {
                        if (item.disabled) {
                            return
                        }
                        this.materialFamilyOptions.set(
                            item.key?.toLowerCase(),
                            item.description ? item.key + ' - ' + item.description : item.key
                        )
                    })
                    return this.baseMaterialService.getAll()
                })
            )
            .pipe(
                map(data =>
                    data.map(item => {
                        return {
                            ...item
                        }
                    })
                ),
                tap(data => {
                    this.pendingSource = [...data]
                    this.fillTracker()
                })
            )
    }

    canSave(): boolean {
        return this.changed && this.noErrorsInEdited && !this.saving
    }

    private convertPayload(): { create?: BaseMaterialItem[]; update?: BaseMaterialItem[] } | [] {
        const fluentTracker = this.fluentTracker.generate()
        const data = {
            create: [],
            update: []
        }
        fluentTracker.forEach(item => {
            if (!item.changed && !item.new) {
                return
            }

            item.disabled = item.disabled === 'true'
            const findElem = this.materialFamilySource.find(
                materialFamilyItem =>
                    item?.materialFamily?.id.toLowerCase() === materialFamilyItem.key.toLowerCase()
            )
            if (findElem) {
                item.materialFamily = { id: findElem.key, code: findElem.description }
            }

            delete item.changed

            if (item.new) {
                delete item.new
                data.create.push({ ...item })
            } else {
                data.update.push({ ...item })
            }
        })
        return data
    }

    save(): void {
        const data = this.convertPayload()
        this.error = null
        this.saved = false
        if (!Array.isArray(data)) {
            const { create, update } = data

            let requests = []

            if (create.length) {
                requests.push(this.baseMaterialService.create(create))
            }

            if (update.length) {
                requests.push(this.baseMaterialService.update(update))
            }

            this.saving = true

            merge(...requests)
                .pipe(
                    finalize(() => {
                        this.pendingSource = []
                        this.fluentTracker.clearAll()
                        this.saving = false
                        this.table.reload()
                    })
                )
                .subscribe(() => {
                    this.saved = true
                }, error => {
                    this.error = error
                })
        }
    }

    private createNewBaseMaterialType(): BaseMaterialItem {
        const id = uuid()
        return {
            key: '',
            description: '',
            materialFamily: null,
            disabled: false,
            rank: null,
            id,
            new: true,
            library: 'baseMaterial',
            namespace: 'kering'
        }
    }

    addNewItem(): void {
        this.pendingSource.splice(this.pendingSource.length + 1, 0, this.createNewBaseMaterialType())
        this.table.reload()
        this.table.goToLastPage()
    }

    private removeItem(id: string) {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(md => md.id === id)
        this.pendingSource.splice(removedIndex, 1)

        this.table.reload()
    }

    private updateExistFields(row): void {
        if (!this.fluentTracker.tracker.hasValue(row.id, 'key')) {
            this.updateValue(row.id, 'key', row.key)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'description')) {
            this.updateValue(row.id, 'description', row.description)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'materialFamily')) {
            this.updateValue(row.id, 'materialFamily', row.materialFamily)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'disabled')) {
            this.updateValue(row.id, 'disabled', row.disabled.toString())
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'rank')) {
            this.updateValue(row.id, 'rank', row.rank)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'new')) {
            this.updateValue(row.id, 'new', row.new)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'library')) {
            this.updateValue(row.id, 'library', row.library)
        }
        if (!this.fluentTracker.tracker.hasValue(row.id, 'namespace')) {
            this.updateValue(row.id, 'namespace', row.namespace)
        }
    }

    private updateFluentValue(value, row, column: string) {
        const indexToUpdate = this.pendingSource.findIndex(mc => mc.id === row.id)
        if (indexToUpdate !== -1) {
            this.pendingSource[indexToUpdate][column] = value
            row[column] = value
        }
        this.updateExistFields(row)
        this.updateValue(row.id, column, value)
    }

    private fillTracker() {
        const fields = Object.keys(this.fluentTracker.trackable.fields)
        this.pendingSource.forEach((row, i) => {
            fields.forEach(f => {
                let value = row[f]
                if (typeof value === 'boolean') {
                    value = value?.toString()
                }
                this.fluentTracker.update(row.id, f, value, { silent: true })
            })
        })

        this.fluentTracker.validateAll()
    }

    private updateValue(id, field, value) {
        this.fluentTracker.update(id, field, value)
        this.fluentTracker.update(id, 'changed', true, { silent: true })
        this.changed = true
    }
}
